import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { mainSiteUrl, categories } from "@/mocks/categories";
import CategoryDropdown from "@/components/base/CategoryDropdown";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    setMobileMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const handleMobileSelect = (url: string) => {
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-background-100 shadow-sm"
          : "bg-white/90 backdrop-blur-sm border-b border-white/20"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-18">
        <Link
          to="/"
          className="text-lg md:text-xl font-heading font-bold tracking-wide whitespace-nowrap cursor-pointer text-foreground-900 hover:text-primary-500 transition-colors"
        >
          ACBuySpreadsheet
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <Link
            to="/"
            className="text-sm font-medium whitespace-nowrap cursor-pointer transition-colors text-foreground-700 hover:text-primary-500"
          >
            Home
          </Link>
          <Link
            to="/acbuyspreadsheet-guide"
            className="text-sm font-medium whitespace-nowrap cursor-pointer transition-colors text-foreground-700 hover:text-primary-500"
          >
            Shopping Guide
          </Link>
          <button
            onClick={() => scrollTo("categories")}
            className="text-sm font-medium whitespace-nowrap cursor-pointer transition-colors text-foreground-700 hover:text-primary-500"
          >
            Categories
          </button>
          <button
            onClick={() => scrollTo("features")}
            className="text-sm font-medium whitespace-nowrap cursor-pointer transition-colors text-foreground-700 hover:text-primary-500"
          >
            Features
          </button>
          <button
            onClick={() => scrollTo("guides")}
            className="text-sm font-medium whitespace-nowrap cursor-pointer transition-colors text-foreground-700 hover:text-primary-500"
          >
            Guides
          </button>
          <button
            onClick={() => scrollTo("reviews")}
            className="text-sm font-medium whitespace-nowrap cursor-pointer transition-colors text-foreground-700 hover:text-primary-500"
          >
            Reviews
          </button>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden md:block">
            <CategoryDropdown />
          </div>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="hidden sm:inline-flex items-center px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 bg-primary-500 text-white hover:bg-primary-600"
          >
            acbuy spreadsheet
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-up-line ml-1.5"></i>
            </span>
          </a>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer rounded-full hover:bg-background-100 transition-colors"
            aria-label="Toggle menu"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              {mobileMenuOpen ? (
                <i className="ri-close-line text-xl text-foreground-900"></i>
              ) : (
                <i className="ri-menu-line text-xl text-foreground-900"></i>
              )}
            </span>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-background-100 max-h-[calc(100vh-64px)] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-50 cursor-pointer transition-colors"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-home-line text-foreground-500"></i>
              </span>
              Home
            </Link>
            <Link
              to="/acbuyspreadsheet-guide"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-50 cursor-pointer transition-colors"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-book-open-line text-foreground-500"></i>
              </span>
              Shopping Guide
            </Link>

            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-50 cursor-pointer transition-colors"
            >
              <span className="flex items-center gap-3">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-grid-line text-foreground-500"></i>
                </span>
                Categories
              </span>
              <span className="w-4 h-4 flex items-center justify-center">
                {mobileCatOpen ? (
                  <i className="ri-arrow-up-s-line text-foreground-400"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-foreground-400"></i>
                )}
              </span>
            </button>

            {mobileCatOpen && (
              <div className="ml-4 pl-4 border-l-2 border-background-100 flex flex-col gap-0.5">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleMobileSelect(cat.url)}
                    className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-accent-50 hover:text-accent-700 cursor-pointer transition-colors"
                  >
                    <span>{cat.name}</span>
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-external-link-line text-[10px] text-foreground-300"></i>
                    </span>
                  </button>
                ))}
              </div>
            )}

            <div className="border-t border-background-100 my-2"></div>

            <button
              onClick={() => { scrollTo("categories"); }}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-50 cursor-pointer transition-colors"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-store-line text-foreground-500"></i>
              </span>
              Browse Categories
            </button>
            <button
              onClick={() => { scrollTo("features"); }}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-50 cursor-pointer transition-colors"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-shapes-line text-foreground-500"></i>
              </span>
              Features
            </button>
            <button
              onClick={() => { scrollTo("guides"); }}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-50 cursor-pointer transition-colors"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-article-line text-foreground-500"></i>
              </span>
              Guides
            </button>

            <div className="border-t border-background-100 my-2"></div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-primary-500 text-white rounded-full text-sm font-semibold cursor-pointer transition-colors hover:bg-primary-600"
            >
              acbuy spreadsheet
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line"></i>
              </span>
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center gap-2 px-4 py-3 mt-1 bg-foreground-900 text-white rounded-full text-sm font-semibold cursor-pointer transition-colors hover:bg-foreground-800"
            >
              buy it now
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-shopping-bag-line"></i>
              </span>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}